import { throttle } from 'lodash-es'
import { RefObject, useCallback, useEffect, useRef } from 'react'
import type { VListHandle } from 'virtua'

/**
 * This hook creates a throttled scroll handler that checks for changes in the visible range
 * of items in a virtua virtual list and calls the provided callback when the range changes.
 *
 * @param virtuaHandleRef - React ref containing the virtua list handle
 * @param onRangeChange - Callback function that receives the new start and end indices
 * @param throttleTime - Time in milliseconds to throttle the scroll handler (default: 200ms)
 *
 * @returns A throttled scroll handler function that should be passed to the virtual list's onScroll prop
 */
export function useRangeChange(
    virtuaHandleRef: RefObject<VListHandle>,
    onRangeChange: (startIndex: number, endIndex: number) => void,
    throttleTime = 200,
) {
    const startIndexRef = useRef(-1)
    const endIndexRef = useRef(-1)

    const throttledOnScroll = useCallback(
        throttle(() => {
            const virtuaHandle = virtuaHandleRef.current
            if (!virtuaHandle) return

            const startIndex = virtuaHandle.findStartIndex()
            const endIndex = virtuaHandle.findEndIndex()

            if (
                startIndex !== startIndexRef.current ||
                endIndex !== endIndexRef.current
            ) {
                onRangeChange(startIndex, endIndex)
            }

            startIndexRef.current = startIndex
            endIndexRef.current = endIndex
        }, throttleTime),
        [onRangeChange, virtuaHandleRef],
    )

    useEffect(() => {
        return () => {
            throttledOnScroll.cancel()
        }
    }, [throttledOnScroll])

    return throttledOnScroll
}
